import cn from 'classnames';
import dynamic from 'next/dynamic';
import React from 'react';

import { useAiSearch } from '@/components/ai-search/AiSearchContext';
import { ETabsStyle, ETabsVariant, Tabs } from '@/components/common/Hero/Tabs';
import { ERideTransitionPlacement } from '@/services/analytics/ride';

import { THeaderCenter } from './headerConfig';

const SearchHeader = dynamic(() => import('../../SearchHeader/SearchHeader'), {
  ssr: true,
});

// Client-only so SSR / first-render markup always matches SearchHeader; the AI
// swap only happens after mount (useAiSearch.available gates on hydration).
const AiSearchHeaderInput = dynamic(() => import('@/components/ai-search/AiSearchHeaderInput'), {
  ssr: false,
});

interface TProps {
  center: THeaderCenter;
}

/**
 * The middle slot of the global header: the rental tabs (home layouts), the
 * search bar / SERP pill, or nothing.
 */
export const HeaderCenter = ({ center }: TProps) => {
  const hasSearchCenter = center.type === 'search';
  const { available, enabled } = useAiSearch();
  // Swap the normal search bar for the AI input only when the renter has opted
  // in (and the experiment is available on this route, post-hydration).
  const useAiInput = hasSearchCenter && available && enabled;

  return (
    <div className={cn('flex', hasSearchCenter ? 'xl:justify-center' : 'justify-center')}>
      {center.type === 'tabs' && (
        <div className="hidden lg:block">
          <Tabs
            tabVariant={ETabsVariant.Rentals}
            style={ETabsStyle.Links}
            ridePlacement={ERideTransitionPlacement.GLOBAL_HEADER_TAB}
          />
        </div>
      )}
      {center.type === 'search' &&
        (useAiInput ? (
          <AiSearchHeaderInput />
        ) : (
          <SearchHeader
            isSERP={center.serp}
            isCampground={center.campground}
            forceSearchPageMode={center.forceSearchPageMode}
            deferNavigationUntilSubmit={center.deferNavigationUntilSubmit}
            canShrink
            redesigned
          />
        ))}
    </div>
  );
};
